import {
  ACCESSORY_STATUS_LABELS,
  ASSET_STATUS_LABELS,
  LICENSE_MODE_LABELS,
  LICENSE_STATUS_LABELS,
} from "@edm/shared";
import { useQuery } from "@tanstack/react-query";
import { Boxes, Laptop, Package, RefreshCw, Tags } from "lucide-react";
import { useMemo, useState } from "react";
import { EmptyState } from "@/components/empty-state";
import { PageHeader } from "@/components/page-header";
import { StatCard } from "@/components/stat-card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  type AccessoryRow,
  type AssetRow,
  type LicenseRow,
  listResource,
  type MaterialRow,
  type StockRow,
  useReferenceData,
} from "@/lib/master-data";
import { formatDate } from "@/lib/utils";

type TabKey = "assets" | "accessories" | "consumables" | "licenses";

const STATUS_VARIANT: Record<string, "default" | "success" | "warning" | "danger" | "secondary"> = {
  in_stock: "success",
  in_use: "default",
  returned: "warning",
  scrapped: "secondary",
  assigned: "default",
  borrowed: "default",
  repairing: "warning",
  disposed: "secondary",
  lost: "danger",
  active: "success",
  expiring: "warning",
  expired: "danger",
  terminated: "secondary",
  renewed: "secondary",
};

export function InventoryPage({ initialTab = "assets" }: { initialTab?: TabKey }) {
  const [tab, setTab] = useState<TabKey>(initialTab);
  const reference = useReferenceData();
  const assets = useQuery({
    queryKey: ["assets"],
    queryFn: () => listResource<AssetRow>("/api/v1/assets"),
  });
  const accessories = useQuery({
    queryKey: ["accessories"],
    queryFn: () => listResource<AccessoryRow>("/api/v1/accessories"),
  });
  const stock = useQuery({
    queryKey: ["consumable-stock"],
    queryFn: () => listResource<StockRow>("/api/v1/consumables/stock"),
  });
  const licenses = useQuery({
    queryKey: ["licenses"],
    queryFn: () => listResource<LicenseRow>("/api/v1/licenses"),
  });
  const materials = useQuery({
    queryKey: ["materials"],
    queryFn: () => listResource<MaterialRow>("/api/v1/materials"),
  });

  const materialById = useMemo(
    () => new Map((materials.data ?? []).map((item) => [item.id, item])),
    [materials.data],
  );
  const users = reference.data?.users ?? [];
  const loading =
    assets.isLoading || accessories.isLoading || stock.isLoading || licenses.isLoading;

  const assetStats = {
    total: assets.data?.length ?? 0,
    inStock: assets.data?.filter((row) => row.status === "in_stock").length ?? 0,
    inUse:
      assets.data?.filter((row) => row.status === "assigned" || row.status === "borrowed").length ??
      0,
  };
  const accessoryStats = {
    total: accessories.data?.length ?? 0,
    inStock: accessories.data?.filter((row) => row.status === "in_stock").length ?? 0,
    inUse: accessories.data?.filter((row) => row.status === "in_use").length ?? 0,
  };
  const stockTotal = stock.data?.reduce((sum, row) => sum + row.quantity, 0) ?? 0;
  const activeLicenses =
    licenses.data?.filter((row) => ["active", "expiring"].includes(row.status)).length ?? 0;

  function refresh() {
    assets.refetch();
    accessories.refetch();
    stock.refetch();
    licenses.refetch();
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="库存视图"
        description="资产按实例、低值易耗按实例、耗材按物料汇总、软件许可按席位统计；所有查询遵守当前账号的数据范围。"
        actions={
          <Button variant="outline" onClick={refresh}>
            <RefreshCw className="h-4 w-4" />
            刷新库存
          </Button>
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="资产在库 / 在用"
          value={`${assetStats.inStock} / ${assetStats.inUse}`}
          hint={`共 ${assetStats.total} 台`}
          icon={Laptop}
        />
        <StatCard
          label="低值易耗在库 / 在用"
          value={`${accessoryStats.inStock} / ${accessoryStats.inUse}`}
          hint={`共 ${accessoryStats.total} 件`}
          icon={Boxes}
          accent="success"
        />
        <StatCard
          label="耗材库存"
          value={stockTotal}
          hint={`${stock.data?.length ?? 0} 种物料`}
          icon={Package}
          accent="warning"
        />
        <StatCard
          label="有效软件许可"
          value={activeLicenses}
          hint={`共 ${licenses.data?.length ?? 0} 份`}
          icon={Tags}
        />
      </div>

      <Tabs value={tab} onValueChange={(value) => setTab(value as TabKey)}>
        <TabsList className="flex-wrap">
          <TabsTrigger value="assets">资产</TabsTrigger>
          <TabsTrigger value="accessories">低值易耗</TabsTrigger>
          <TabsTrigger value="consumables">耗材</TabsTrigger>
          <TabsTrigger value="licenses">软件许可</TabsTrigger>
        </TabsList>
      </Tabs>

      <Card>
        <CardHeader>
          <CardTitle>
            {tab === "assets"
              ? "资产库存"
              : tab === "accessories"
                ? "低值易耗库存"
                : tab === "consumables"
                  ? "耗材库存"
                  : "软件许可"}
          </CardTitle>
          <CardDescription>数据来自台账与库存结存；补货/到期提醒在 M5 接入。</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              {["i1", "i2", "i3", "i4"].map((key) => (
                <Skeleton key={key} className="h-11 w-full" />
              ))}
            </div>
          ) : tab === "assets" ? (
            (assets.data ?? []).length === 0 ? (
              <EmptyState title="暂无资产" description="从资产台账登记入库。" />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>资产编号</TableHead>
                    <TableHead>物料</TableHead>
                    <TableHead>状态</TableHead>
                    <TableHead>使用人</TableHead>
                    <TableHead>位置</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(assets.data ?? []).map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="font-mono text-xs">{row.assetCode}</TableCell>
                      <TableCell>{materialById.get(row.materialId)?.name ?? "—"}</TableCell>
                      <TableCell>
                        <Badge variant={STATUS_VARIANT[row.status] ?? "secondary"}>
                          {ASSET_STATUS_LABELS[row.status as keyof typeof ASSET_STATUS_LABELS] ??
                            row.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {users.find((user) => user.id === row.holderId)?.displayName ?? "—"}
                      </TableCell>
                      <TableCell>{row.locationText ?? "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )
          ) : tab === "accessories" ? (
            (accessories.data ?? []).length === 0 ? (
              <EmptyState title="暂无低值易耗" description="先入库低值易耗实例。" />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>编号</TableHead>
                    <TableHead>物料</TableHead>
                    <TableHead>状态</TableHead>
                    <TableHead>使用人</TableHead>
                    <TableHead>位置</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(accessories.data ?? []).map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="font-mono text-xs">{row.code}</TableCell>
                      <TableCell>{materialById.get(row.materialId)?.name ?? "—"}</TableCell>
                      <TableCell>
                        <Badge variant={STATUS_VARIANT[row.status] ?? "secondary"}>
                          {ACCESSORY_STATUS_LABELS[
                            row.status as keyof typeof ACCESSORY_STATUS_LABELS
                          ] ?? row.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {users.find((user) => user.id === row.holderId)?.displayName ?? "—"}
                      </TableCell>
                      <TableCell>{row.location ?? "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )
          ) : tab === "consumables" ? (
            (stock.data ?? []).length === 0 ? (
              <EmptyState title="暂无耗材库存" description="从耗材入库开始建立库存。" />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>物料编码</TableHead>
                    <TableHead>名称</TableHead>
                    <TableHead>库存</TableHead>
                    <TableHead>单位</TableHead>
                    <TableHead>更新时间</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(stock.data ?? []).map((row) => (
                    <TableRow key={row.materialId}>
                      <TableCell className="font-mono text-xs">{row.materialCode}</TableCell>
                      <TableCell className="font-medium">{row.materialName}</TableCell>
                      <TableCell>{row.quantity}</TableCell>
                      <TableCell>{row.unit}</TableCell>
                      <TableCell>{formatDate(row.updatedAt)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )
          ) : (licenses.data ?? []).length === 0 ? (
            <EmptyState title="暂无软件许可" description="在软件许可页登记许可与席位。" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>许可编号</TableHead>
                  <TableHead>软件</TableHead>
                  <TableHead>模式</TableHead>
                  <TableHead>席位</TableHead>
                  <TableHead>到期日期</TableHead>
                  <TableHead>状态</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(licenses.data ?? []).map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="font-mono text-xs">{row.licenseCode}</TableCell>
                    <TableCell>{materialById.get(row.materialId)?.name ?? "—"}</TableCell>
                    <TableCell>
                      {LICENSE_MODE_LABELS[row.mode as keyof typeof LICENSE_MODE_LABELS] ??
                        row.mode}
                    </TableCell>
                    <TableCell>
                      {row.allocatedSeats} / {row.totalSeats}
                    </TableCell>
                    <TableCell>{formatDate(row.expiryDate)}</TableCell>
                    <TableCell>
                      <Badge variant={STATUS_VARIANT[row.status] ?? "secondary"}>
                        {LICENSE_STATUS_LABELS[row.status as keyof typeof LICENSE_STATUS_LABELS] ??
                          row.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
